<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['confirm', 'close']);

const { t } = useI18n();

const dialogRef = ref(null);
const mode = ref('specific');
const selectedInboxIds = ref([]);

const inboxes = useMapGetter('inboxes/getInboxes');
const allConversations = useMapGetter('getAllConversations');

// Best-effort per-inbox unread count from the conversations currently loaded
// in the store. This is approximate (only reflects the loaded pages of the
// current view), but it is informative enough to help the user choose which
// inboxes to mark.
const unreadCountByInbox = computed(() => {
  const counts = {};
  const list = allConversations.value || [];
  list.forEach(conversation => {
    if (!conversation || !(conversation.unread_count > 0)) return;
    const inboxId = conversation.inbox_id;
    if (!inboxId) return;
    counts[inboxId] = (counts[inboxId] || 0) + 1;
  });
  return counts;
});

const totalApproxUnread = computed(() => {
  return Object.values(unreadCountByInbox.value).reduce(
    (sum, count) => sum + count,
    0
  );
});

const visibleInboxes = computed(() => {
  const list = inboxes.value || [];
  // Sort: inboxes with unread first (descending), then by name
  return [...list].sort((a, b) => {
    const ua = unreadCountByInbox.value[a.id] || 0;
    const ub = unreadCountByInbox.value[b.id] || 0;
    if (ua !== ub) return ub - ua;
    return (a.name || '').localeCompare(b.name || '');
  });
});

const allInboxesSelected = computed({
  get() {
    return (
      visibleInboxes.value.length > 0 &&
      selectedInboxIds.value.length === visibleInboxes.value.length
    );
  },
  set(value) {
    selectedInboxIds.value = value
      ? visibleInboxes.value.map(inbox => inbox.id)
      : [];
  },
});

const canConfirm = computed(() => {
  if (mode.value === 'all') return true;
  return selectedInboxIds.value.length > 0;
});

function reset() {
  mode.value = 'specific';
  selectedInboxIds.value = [];
}

function open() {
  reset();
  dialogRef.value?.open();
}

function close() {
  dialogRef.value?.close();
}

function onConfirm() {
  if (!canConfirm.value || props.isLoading) return;
  if (mode.value === 'all') {
    emit('confirm', { inboxIds: null });
  } else {
    emit('confirm', { inboxIds: [...selectedInboxIds.value] });
  }
}

function onClose() {
  emit('close');
}

// Reset selections whenever the mode changes so a stale specific-mode
// selection doesn't leak into a later "all" decision (and vice versa).
watch(mode, () => {
  if (mode.value === 'all') {
    selectedInboxIds.value = [];
  }
});

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="alert"
    width="lg"
    :title="t('CHAT_LIST.MARK_ALL_READ.TITLE')"
    :confirm-button-label="t('CHAT_LIST.MARK_ALL_READ.CONFIRM_BTN')"
    :cancel-button-label="t('CHAT_LIST.MARK_ALL_READ.CANCEL_BTN')"
    :disable-confirm-button="!canConfirm"
    :is-loading="isLoading"
    surface="light"
    @confirm="onConfirm"
    @close="onClose"
  >
    <div class="flex flex-col gap-4">
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CHAT_LIST.MARK_ALL_READ.DESCRIPTION') }}
      </p>

      <!-- Mode: Specific inboxes -->
      <div class="flex flex-col gap-2">
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="mode"
            type="radio"
            value="specific"
            class="mark-all-read-radio"
          />
          <span class="font-medium text-n-slate-12">
            {{ t('CHAT_LIST.MARK_ALL_READ.SPECIFIC_INBOXES') }}
          </span>
        </label>

        <div
          v-if="mode === 'specific'"
          class="ml-6 border border-n-slate-4 rounded-lg p-2 max-h-64 overflow-y-auto bg-n-alpha-1"
        >
          <div
            v-if="!visibleInboxes.length"
            class="p-2 text-sm text-n-slate-11"
          >
            {{ t('CHAT_LIST.MARK_ALL_READ.NO_INBOXES') }}
          </div>

          <label
            v-else
            class="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-n-alpha-2 rounded border-b border-n-slate-3 mb-1"
          >
            <input
              v-model="allInboxesSelected"
              type="checkbox"
              class="mark-all-read-checkbox"
            />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('CHAT_LIST.MARK_ALL_READ.SELECT_ALL_VISIBLE') }}
            </span>
          </label>

          <label
            v-for="inbox in visibleInboxes"
            :key="inbox.id"
            class="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-n-alpha-2 rounded"
          >
            <input
              v-model="selectedInboxIds"
              type="checkbox"
              :value="inbox.id"
              class="mark-all-read-checkbox"
            />
            <span class="flex-1 text-sm text-n-slate-12 truncate">
              {{ inbox.name }}
            </span>
            <span
              v-if="unreadCountByInbox[inbox.id]"
              class="text-xs px-2 py-0.5 rounded-full bg-n-amber-3 text-n-amber-11"
            >
              {{ unreadCountByInbox[inbox.id] }}
            </span>
          </label>
        </div>
      </div>

      <!-- Mode: All inboxes -->
      <div class="flex flex-col gap-2">
        <label class="flex items-start gap-2 cursor-pointer">
          <input
            v-model="mode"
            type="radio"
            value="all"
            class="mark-all-read-radio mt-1"
          />
          <div class="flex-1">
            <div class="font-medium text-n-slate-12">
              {{ t('CHAT_LIST.MARK_ALL_READ.ALL_INBOXES') }}
            </div>
            <div class="text-xs text-n-ruby-10 mt-0.5">
              {{ t('CHAT_LIST.MARK_ALL_READ.ALL_INBOXES_WARNING') }}
            </div>
          </div>
        </label>
      </div>

      <!-- Footer hint -->
      <div
        v-if="totalApproxUnread > 0"
        class="text-xs text-n-slate-10 -mt-1 ml-1"
      >
        {{
          t('CHAT_LIST.MARK_ALL_READ.APPROX_HINT', { count: totalApproxUnread })
        }}
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
.mark-all-read-radio,
.mark-all-read-checkbox {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #2563eb;
}
</style>
